export type ResourceCategory =
  | 'financing'
  | 'legal'
  | 'market-research'
  | 'property-analysis'
  | 'property-management';

export type ResourceDifficulty = 'beginner' | 'intermediate' | 'advanced';

export type ResourceArticle = {
  slug: string;
  title: string;
  description: string;
  category: ResourceCategory;
  difficulty: ResourceDifficulty;
  readMinutes: number;
  publishedAt: string;
  tags: string[];
  /** Optional longer body for detail view */
  body?: string;
};

export const RESOURCE_CATEGORY_LABEL: Record<ResourceCategory, string> = {
  financing: 'Financing',
  legal: 'Legal considerations',
  'market-research': 'Market research',
  'property-analysis': 'Property analysis',
  'property-management': 'Property management',
};

export const RESOURCE_ARTICLES: ResourceArticle[] = [
  {
    slug: 'agency-debt-fannie-freddie',
    title: 'Agency debt: Fannie Mae and Freddie Mac loans',
    description:
      'Learn about government-sponsored enterprise loans for multifamily properties, execution options, and how sponsors qualify.',
    category: 'financing',
    difficulty: 'advanced',
    readMinutes: 10,
    publishedAt: '2024-02-09',
    tags: ['agency-debt', 'fannie-mae', 'freddie-mac', 'multifamily'],
  },
  {
    slug: 'tenant-retention-strategies',
    title: 'Proven tenant retention strategies for multifamily',
    description:
      'Practical tactics to reduce turnover, strengthen community, and protect NOI without giving away the store.',
    category: 'property-management',
    difficulty: 'beginner',
    readMinutes: 8,
    publishedAt: '2024-01-22',
    tags: ['retention', 'leasing', 'noi'],
  },
  {
    slug: 'multifamily-due-diligence-checklist',
    title: 'The complete multifamily due diligence checklist',
    description:
      'A structured walkthrough of financial, physical, and legal workstreams so nothing critical is missed before closing.',
    category: 'property-analysis',
    difficulty: 'advanced',
    readMinutes: 12,
    publishedAt: '2024-02-14',
    tags: ['due-diligence', 'underwriting', 'closing'],
  },
  {
    slug: 'understanding-cap-rates-multifamily',
    title: 'Understanding cap rates in multifamily investing',
    description:
      'How cap rate relates to risk, growth, and leverage—and when to rely on it versus other return metrics.',
    category: 'property-analysis',
    difficulty: 'beginner',
    readMinutes: 6,
    publishedAt: '2024-03-01',
    tags: ['cap-rate', 'noi', 'valuation'],
  },
  {
    slug: 'multifamily-lease-agreements-guide',
    title: 'Essential guide to multifamily lease agreements',
    description:
      'Key clauses, rent concessions, renewal mechanics, and operator pitfalls that affect collections and compliance.',
    category: 'legal',
    difficulty: 'intermediate',
    readMinutes: 10,
    publishedAt: '2024-02-17',
    tags: ['leases', 'legal', 'contracts'],
  },
  {
    slug: 'fair-housing-compliance',
    title: 'Fair housing compliance for multifamily owners',
    description:
      'Foundational practices for advertising, screening, and accommodations—plus where many portfolios get tripped up.',
    category: 'legal',
    difficulty: 'beginner',
    readMinutes: 7,
    publishedAt: '2024-01-30',
    tags: ['fair-housing', 'compliance', 'legal'],
  },
  {
    slug: 'identifying-emerging-multifamily-markets',
    title: 'Identifying emerging multifamily markets',
    description:
      'Demographics, employment, supply pipeline, and risk factors to stress test before you stretch on basis.',
    category: 'market-research',
    difficulty: 'intermediate',
    readMinutes: 9,
    publishedAt: '2024-02-21',
    tags: ['market-research', 'emerging-markets', 'demographics'],
  },
  {
    slug: 'bridge-lending-value-add',
    title: 'Bridge lending for value-add multifamily',
    description:
      'How floating-rate bridge fits renovation and lease-up timelines—and what covenants to watch in the term sheet.',
    category: 'financing',
    difficulty: 'advanced',
    readMinutes: 11,
    publishedAt: '2024-02-05',
    tags: ['bridge', 'value-add', 'floating-rate'],
  },
  {
    slug: 'noi-normalization-t12',
    title: 'NOI normalization from trailing operations',
    description:
      'Separating owner-specific costs, one-timers, and true market expenses so your offer reflects sustainable cash flow.',
    category: 'property-analysis',
    difficulty: 'advanced',
    readMinutes: 11,
    publishedAt: '2024-03-08',
    tags: ['noi', 't12', 'normalization'],
  },
  {
    slug: 'property-tax-appeals-multifamily',
    title: 'Property tax appeals for multifamily assets',
    description:
      'When reassessment risk is largest post-closing and how to model tax resets in your pro forma.',
    category: 'legal',
    difficulty: 'intermediate',
    readMinutes: 8,
    publishedAt: '2024-02-11',
    tags: ['property-tax', 'appeals', 'expenses'],
  },
  {
    slug: 'vendor-management-best-practices',
    title: 'Vendor management best practices',
    description:
      'Setting scopes, insurance requirements, and payment timing to keep turns on schedule and liability contained.',
    category: 'property-management',
    difficulty: 'beginner',
    readMinutes: 6,
    publishedAt: '2024-01-18',
    tags: ['vendors', 'operations', 'capex'],
  },
  {
    slug: 'rent-growth-and-loss-to-lease',
    title: 'Rent growth and loss to lease',
    description:
      'Bridging in-place rents to market, renewal tradeouts, and how to express upside in underwriting assumptions.',
    category: 'market-research',
    difficulty: 'intermediate',
    readMinutes: 7,
    publishedAt: '2024-02-26',
    tags: ['rent-growth', 'loss-to-lease', 'underwriting'],
  },
];

export function getResourceArticleBySlug(slug: string): ResourceArticle | undefined {
  return RESOURCE_ARTICLES.find((a) => a.slug === slug);
}
